import CloseIcon from "@mui/icons-material/Close";
import MinimizeIcon from "@mui/icons-material/Remove";
import { Box, Button, Grid, Paper, Typography, styled } from "@mui/material";
import { HTMLAttributes, forwardRef } from "react";
import { isDesktop } from "react-device-detect";
import { convertHexToRGBA } from "utils/commonUtils";
import { brightenColor, getInvertColor } from "utils/themeUtils";

const titlebarHeight = 52;
const WindowTitle = styled(Typography)(({ theme }) => ({
	fontSize: "15px",
	fontWeight: 700,
	padding: "8px 0",
	color: getInvertColor(theme.palette.primary.main),
	// ellipsis
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	flex: 1,
	[theme.breakpoints.down("smallMobile")]: {
		fontSize: "14px",
		padding: 0,
	},
}));

const TitleBar = styled("div")(({ theme }) => ({
	backgroundColor: theme.palette.primary.main,
	width: "100%",
}));

const ActionButton = styled(Button)(({ theme }) => ({
	borderRadius: 0,
	color: getInvertColor(theme.palette.primary.main),
	"&:hover": {
		background: "#FFFFFF2E",
	},
	height: titlebarHeight,
}));

const CloseButton = styled(Button)(({ theme }) => ({
	borderRadius: 0,
	color: getInvertColor(theme.palette.primary.main),
	"&:hover": {
		background: brightenColor(theme.palette.primary.main, 4),
	},
	height: titlebarHeight,
}));

export const ActionBar = styled("div")(({ theme }) => ({
	backgroundColor: brightenColor(theme.palette.primary.main, 20),
	minHeight: titlebarHeight,
	width: "100%",
	boxShadow: "0px 15px 40px #00000083",
	display: "flex",
	alignItems: "center",
	[theme.breakpoints.down("mobile")]: {
		backgroundColor: convertHexToRGBA(theme.palette.primary.main, 1),
		opacity: 1,
		justifyContent: "center",
	},
}));

const IconContainer = styled("div")(({ theme }) => ({
	margin: "0 12px 0 20px",
	display: "flex",
	[theme.breakpoints.down("smallMobile")]: {
		margin: "0 10px",
	},
}));

export const WindowTitleBar = (props: {
	icon?: JSX.Element;
	title: string;
	onMinimize?: () => void;
	onClose?: () => void;
	children?: React.ReactNode;
}) => {
	const { title, icon, onMinimize, onClose, children } = props;
	return (
		<TitleBar className="title-bar">
			<Grid container alignItems="center" sx={{ flexWrap: "nowrap", width: "100%" }}>
				<Grid
					item
					sx={{
						display: "flex",
						alignItems: "center",

						maxWidth: "calc(100% - 130px)",
					}}
					xs={children ? 3 : 6}
				>
					{icon && <IconContainer>{icon}</IconContainer>}
					<WindowTitle id="text-window-title" title={title}>
						{title}
					</WindowTitle>
				</Grid>
				<Grid item sx={{ flexGrow: 1, textAlign: "center" }}>
					{children}
				</Grid>
				<Grid
					item
					sx={{
						display: "flex",
						flexWrap: "nowrap",
						justifyContent: "flex-end",
					}}
					xs={children ? 3 : 6}
				>
					{isDesktop && onMinimize && (
						<ActionButton onClick={onMinimize}>
							<MinimizeIcon />
						</ActionButton>
					)}
					{onClose && (
						<CloseButton onClick={onClose} className="closeButton" data-testid="close-window-btn">
							<CloseIcon />
						</CloseButton>
					)}
				</Grid>
			</Grid>
		</TitleBar>
	);
};

export const WindowContainer = styled(Paper)(({ theme }) => ({
	borderRadius: 8,
	background: "transparent",
	border: `1px solid ${brightenColor(theme.palette.primary.main, 50)}`,
	height: "100%",
	width: "100%",
	display: "flex",
	flexDirection: "column",
	overflow: "hidden",
	boxShadow: "0px 15px 40px #00000019",
	[theme.breakpoints.down("mobile")]: {
		borderRadius: 0,
	},
}));

export const WindowContent = forwardRef<
	HTMLDivElement,
	HTMLAttributes<HTMLDivElement> & { verticalScroll?: boolean; horizontalScroll?: boolean }
>((props, ref) => {
	const { verticalScroll = true, horizontalScroll = true } = props;
	const { style, ...rest } = props;
	return (
		<Box
			ref={ref}
			sx={{
				overflow: "auto",
				flexGrow: 1,
				background: "#fff",
				boxSizing: "border-box",
				overflowY: verticalScroll ? "auto" : "hidden",
				overflowX: horizontalScroll ? "auto" : "hidden",
				...style,
			}}
			{...rest}
		/>
	);
});
